import * as React from 'react'
import { AppsLink, AppsRoutes } from '@wellesley/router'
import * as TabsPrimitive from '@radix-ui/react-tabs'

import { cn } from '@ui/lib/utils'

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    useDefaults?: boolean
  }
>(({ className, useDefaults = true, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    data-testid="tabsList"
    className={
      useDefaults
        ? cn(
            'inline-flex w-full items-center justify-start gap-s p-none text-gray-500',
            'relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-full after:bg-gray-200',
            className,
          )
        : className
    }
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    isFullWidthTab?: boolean
  }
>(({ className, children, isFullWidthTab, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    asChild
    className={
      className ??
      cn(
        'inline-flex items-center justify-center whitespace-nowrap text-base',
        'z-2 transition-background h-m cursor-pointer focus-visible:outline-none',
        'font-semibold focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
        'group relative text-gray-500 hover:[&_svg]:fill-gray-900',
        'after:absolute after:bottom-0 after:left-0 after:z-[1] after:h-0.5 after:w-full data-[state=active]:after:bg-gray-900',
        { 'flex-1': isFullWidthTab },
      )
    }
    {...props}
  >
    {children}
  </TabsPrimitive.Trigger>
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

type TabControlsProps = React.PropsWithChildren & {
  items: TabItemProps[]
  className?: string
}

type TabItemProps = {
  value: string
  icon?: React.ReactNode
  label?: string
  href?: AppsRoutes
  asChild?: boolean
  className?: string
  isFullWidthTab?: boolean
  disabled?: boolean
}

const TabItem: React.FC<TabItemProps> = ({
  icon,
  label,
  value,
  href,
  asChild,
  className,
  isFullWidthTab,
  disabled,
}) => {
  const content = (
    <span
      data-testid={`tab-item-${value}`}
      className="flex items-center justify-center gap-xxs group-hover:text-gray-900 group-data-[state=active]:text-gray-900 [&_svg]:pointer-events-none"
    >
      {icon}
      {label}
    </span>
  )

  return (
    <TabsTrigger
      value={value}
      className={className}
      disabled={disabled}
      asChild={asChild}
      isFullWidthTab={isFullWidthTab}
    >
      {href ? (
        <AppsLink
          to={href}
          className="flex h-full w-full items-center justify-center"
        >
          {content}
        </AppsLink>
      ) : (
        content
      )}
    </TabsTrigger>
  )
}

const TabsControls: React.FC<TabControlsProps> = ({
  items,
  children,
  className,
}) => (
  <TabsList
    className={cn('no-scrollbar z-0 mt-m overflow-x-auto px-m', className)}
  >
    {items.map((item) => (
      <TabItem key={item.value} isFullWidthTab={items.length <= 3} {...item} />
    ))}
    {children}
  </TabsList>
)

export {
  Tabs,
  TabsContent,
  TabsControls,
  type TabItemProps,
  TabsTrigger,
  TabsList,
}
